import type { DailyTacticalReadiness } from '../../services/operationsApi';
import type { FableMarketView, FableOpsAutomationView } from '../../hooks/useFableLab';
import { formatKst } from './fableLabels';

/** 다음에 할 일과 실행 가능 조건을 명시하는 패널. */
export default function FableNextAction({ readiness, market, opsAutomation }: Props) {
  const conditions = buildConditions(readiness, market);
  const alert = opsAutomation?.blockerAlert;
  const needsReview = Boolean(alert?.alert_required);
  return (
    <section className="fable-panel fable-next">
      <header className="fable-panel-head">
        <h3>지금 사용자께서 할 일</h3>
        <p>
          {needsReview
            ? `검증 문제 ${alert?.blockers.length || 1}건의 원인과 영향 확인이 필요합니다. 수정은 별도 승인 전까지 시작하지 않습니다.`
            : readiness.can_start_official_stage_a
              ? '4주간 공식 관찰을 시작할지 승인 여부를 결정하면 됩니다.'
              : '지금 승인하거나 실행할 일은 없습니다. 시스템이 아래 조건을 기다리는 중입니다.'}
        </p>
      </header>
      <h4 className="fable-subhead">시스템이 기다리는 조건</h4>
      <ul className="fable-cond-list">
        {conditions.map((c) => (
          <li key={c.label} className={c.met ? 'met' : 'unmet'}>
            <span aria-hidden>{c.met ? '✓' : '·'}</span>
            {c.label}
          </li>
        ))}
      </ul>
      <div className="fable-next-notes">
        <p className="fable-go-note">
          4주간 공식 관찰 시작은 <strong>{readiness.official_stage_clock_started ? '이미 시작됐습니다.' : '아직 아닙니다.'}</strong>
          {!readiness.official_stage_clock_started && ' 사용자 승인 후에만 시작합니다.'}
          {readiness.can_start_official_stage_a
            ? ' 시스템 점검상 시작 조건은 충족됐습니다.'
            : ' 시작 전에 시스템이 처리할 조건이 남아 있습니다.'}
        </p>
        <MarketLine market={market} />
      </div>
    </section>
  );
}

function MarketLine({ market }: { market: FableMarketView | null }) {
  if (!market) return <p>미국장 상태: 데이터 없음</p>;
  const next = market.isOpen
    ? `다음 마감: ${formatKst(market.nextClose)} (한국시간)`
    : `다음 개장: ${formatKst(market.nextOpen)} (한국시간)`;
  const detail = market.nextOpen || market.nextClose ? next : market.nextEventText || '다음 일정 데이터 없음';
  return (
    <p>
      미국장 상태: <strong>{market.statusText}</strong> · {detail}
    </p>
  );
}

function buildConditions(r: DailyTacticalReadiness, market: FableMarketView | null) {
  const s = r.live_order_safety;
  return [
    { label: '검증에 사용할 실제 AI 모델이 연결됨', met: r.flags.real_model_enabled },
    { label: '현재 단계가 사전 리허설임', met: r.flags.cohort_mode === 'rehearsal' },
    { label: '공식 시작 전이므로 자동 실행이 꺼져 있음', met: !r.flags.scheduler_enabled },
    { label: '실제 주문과 실제 돈 사용이 잠겨 있음', met: !s.broker_submit_allowed && !s.real_capital_allowed },
    { label: '미국장 열림 (개장 중에만 실행 의미 있음)', met: Boolean(market?.isOpen) },
  ];
}

type Props = {
  readiness: DailyTacticalReadiness;
  market: FableMarketView | null;
  opsAutomation: FableOpsAutomationView | null;
};
